// src/components/Drivers/Drivers.js

import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../../axiosConfig";
import { VENCIMIENTOS_CONDUCTOR } from "./Form/VencimientosDocumentosInfo";
import {
  Table,
  Button,
  Input,
  Pagination,
  Spin,
  message,
  Select,
  Modal,
  Typography,
  Tag,
  Dropdown,
  Tooltip,
} from "antd";
import {
  EllipsisOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  PlusOutlined,
  HistoryOutlined,
  CarOutlined,
  MobileOutlined,
  DownloadOutlined,
  KeyOutlined,
  HighlightOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import AddDriverForm from "./Form/AddDriverForm";
import ViewDriverDialog from "./ViewDriverDialog";
import HistorialConductorDialog from "./HistorialConductorDialog";
import HistorialDispositivosDialog from "./HistorialDispositivosDialog";
import { useUser } from "../../../UserContext";
import "./Drivers.css";
import {
  UserOutlined,
  IdcardOutlined,
  TeamOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { Card, Avatar, Grid } from "antd";
import DriverTransfersModal from "./DriverTransferModal";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const Drivers = () => {
  const { user } = useUser();
  const [drivers, setDrivers] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const [selectedZona, setSelectedZona] = useState(null);
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [filterDocVencidos, setFilterDocVencidos] = useState(false);
  const [filterConVehiculo, setFilterConVehiculo] = useState(null); // null, 'con', 'sin'
  const [filterConductorPropio, setFilterConductorPropio] = useState(null); // null, 'si', 'no', 'sin_definir'
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [listLoading, setListLoading] = useState(false);

  // Estados para el historial del conductor
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [selectedConductorId, setSelectedConductorId] = useState(null);

  //Estados para el historial de traslados
  const [transfersModalOpen, setTransfersModalOpen] = useState(false);

  // Estados para el historial de dispositivos
  const [dispositivosDialogOpen, setDispositivosDialogOpen] = useState(false);
  const [selectedConductorNombre, setSelectedConductorNombre] = useState(null);

  // Estado para exportación
  const [exportLoading, setExportLoading] = useState(false);

  // Estado para reinicio de contraseña
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [driverToResetPassword, setDriverToResetPassword] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  const screens = useBreakpoint();

  //Funcion para permisos
  const { user: currentUser } = useUser();

  const hasPermission = useCallback(
    (moduleKey, permission) => {
      // 1. Verificar si el módulo está disponible
      const module = currentUser?.modulos?.find((m) => m.key === moduleKey);
      if (!module || module.estado !== "disponible") return false;

      // 2. Verificar permisos específicos
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === moduleKey && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Función para obtener conductores con manejo de carga
  const fetchDrivers = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await api.get("/conductores");
      setDrivers(response.data);
      console.log("Conductores cargados:", response.data);
    } catch (error) {
      console.error("Error al obtener los conductores:", error);
      message.error("Error al obtener los conductores");
    } finally {
      setListLoading(false);
    }
  }, []);

  // Función para exportar conductores a Excel
  const handleExportExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      const response = await api.get("/conductores/exportar-excel", {
        params: { search: filter },
        responseType: "blob",
      });

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `conductores_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Excel exportado correctamente");
    } catch (error) {
      console.error("Error al exportar conductores:", error);
      message.error("Error al exportar conductores");
    } finally {
      setExportLoading(false);
    }
  }, [filter]);

  // Función para obtener tipos de documento
  const fetchTiposDocumento = useCallback(async () => {
    try {
      const response = await api.get("/tipos-documento");
      setTiposDocumento(response.data);
    } catch (error) {
      console.error("Error al obtener tipos de documento:", error);
      message.error("Error al obtener tipos de documento");
    }
  }, []);

  // Función para obtener zonas disponibles según el rol del usuario
  const fetchZonas = useCallback(async () => {
    setLoadingZonas(true);
    try {
      const response = await api.get("/conductores/zonas-disponibles");
      setZonas(response.data.zonas || []);
    } catch (error) {
      console.error("Error al obtener las zonas:", error);
      message.error("Error al obtener las zonas");
    } finally {
      setLoadingZonas(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
    fetchTiposDocumento();
    fetchZonas();
  }, [fetchDrivers, fetchTiposDocumento, fetchZonas]);

  useEffect(() => {
    setPage(1);
  }, [filter, selectedZona, selectedEstado, filterDocVencidos, filterConVehiculo, filterConductorPropio]);

  const handleSearchChange = (event) => {
    setFilter(event.target.value);
    setPage(1);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (current, size) => {
    setRowsPerPage(size);
    setPage(1);
  };

  const handleEditClick = useCallback(async (driver) => {
    try {
      setListLoading(true);
      const response = await api.get(`/conductores/${driver.id}`);
      setSelectedDriver(response.data);
      setEditDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener el conductor:", error);
      message.error("Error al obtener los datos del conductor");
    } finally {
      setListLoading(false);
    }
  }, []);

  const handleViewClick = useCallback(async (driver) => {
    try {
      setListLoading(true);
      const response = await api.get(`/conductores/${driver.id}`);
      setSelectedDriver(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener el conductor:", error);
      message.error("Error al obtener los datos del conductor");
    } finally {
      setListLoading(false);
    }
  }, []);

  // Función para abrir el diálogo de reinicio de contraseña
  const handleOpenResetPasswordDialog = useCallback((driver) => {
    setDriverToResetPassword(driver);
    setResetPasswordDialogOpen(true);
  }, []);

  // Reiniciar firma: borra la firma registrada y la app de conductores la vuelve a solicitar
  const handleReiniciarFirma = useCallback((driver) => {
    Modal.confirm({
      title: "Reiniciar firma",
      content: `Se borrará la firma registrada de ${driver.nombre} y la app le solicitará registrarla nuevamente. ¿Desea continuar?`,
      okText: "Reiniciar firma",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          const response = await api.post(`/conductores/${driver.id}/reiniciar-firma`);
          message.success(response.data?.message || "Firma reiniciada");
        } catch (error) {
          message.error(error.response?.data?.message || "Error al reiniciar la firma del conductor");
        }
      },
    });
  }, []);

  // Función para confirmar el reinicio de contraseña
  const confirmResetPassword = async () => {
    if (!driverToResetPassword) return;

    setResetPasswordLoading(true);
    try {
      await api.post(`/conductores/${driverToResetPassword.id}/reiniciar-password`, {
        usuario_id: user.id,
      });
      message.success(`Contraseña de ${driverToResetPassword.nombre} reiniciada exitosamente. La nueva contraseña es: mogotax123`);
      setResetPasswordDialogOpen(false);
      setDriverToResetPassword(null);
    } catch (error) {
      console.error("Error al reiniciar contraseña:", error);
      message.error("Error al reiniciar la contraseña del conductor");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Función para cancelar el reinicio de contraseña
  const cancelResetPassword = () => {
    setResetPasswordDialogOpen(false);
    setDriverToResetPassword(null);
  };

  // Función para manejar la apertura del historial
  const handleHistorialClick = useCallback((driver) => {
    setSelectedConductorId(driver.id);
    setHistorialDialogOpen(true);
  }, []);

  // Función para verificar si un conductor tiene documentos vencidos
  const hasExpiredDocuments = useCallback((driver) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const documentosVencimiento = [
      { campo: 'fecha_vencimiento_licencia', nombre: 'Licencia de Conducción' },
      { campo: 'fecha_vigencia_arl', nombre: 'ARL' },
      // Vencimientos opcionales: solo cuentan si tienen fecha
      ...VENCIMIENTOS_CONDUCTOR,
    ];

    const vencidos = documentosVencimiento.filter(doc => {
      const fecha = driver[doc.campo];
      if (!fecha) return false;
      const fechaVenc = new Date(fecha);
      return fechaVenc < today;
    });

    return {
      hasExpired: vencidos.length > 0,
      expiredDocs: vencidos.map(d => d.nombre),
    };
  }, []);

  const filteredDrivers = useMemo(() => {
    return drivers.filter((driver) => {
      const matchesFilter =
        driver.nombre.toLowerCase().includes(filter.toLowerCase()) ||
        driver.numero_documento.toLowerCase().includes(filter.toLowerCase());

      const matchesZona = selectedZona
        ? driver.zona?.nombre === selectedZona
        : true;

      const matchesEstado = selectedEstado
        ? (driver.estado || '').toLowerCase() === selectedEstado.toLowerCase()
        : true;

      const matchesDocVencidos = filterDocVencidos
        ? hasExpiredDocuments(driver).hasExpired
        : true;

      const matchesVehiculo = filterConVehiculo === null
        ? true
        : filterConVehiculo === 'con'
          ? !!driver.vehiculo_asignado
          : !driver.vehiculo_asignado;

      const matchesConductorPropio = filterConductorPropio === null || filterConductorPropio === undefined
        ? true
        : filterConductorPropio === 'si'
          ? driver.conductor_propio === true
          : filterConductorPropio === 'no'
            ? driver.conductor_propio === false
            : driver.conductor_propio !== true && driver.conductor_propio !== false;

      return matchesFilter && matchesZona && matchesEstado && matchesDocVencidos && matchesVehiculo && matchesConductorPropio;
    });
  }, [drivers, filter, selectedZona, selectedEstado, filterDocVencidos, filterConVehiculo, filterConductorPropio, hasExpiredDocuments]);

  const paginatedDrivers = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredDrivers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredDrivers, page, rowsPerPage]);

  const columns = useMemo(
    () => [
      {
        title: "Nombre",
        dataIndex: "nombre",
        key: "nombre",
        render: (text, driver) => (
          <div className="driver-info-container">
            <div className="driver-info-details">
              <div className="driver-name">
                {driver.nombre}
                {driver.conductor_propio === true && (
                  <Tooltip title="Conductor propio / interno">
                    <Tag color="green" style={{ marginLeft: 6 }}>Propio</Tag>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        ),
        sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      },
      {
        title: "Tipo de Documento",
        dataIndex: "tipo_documento_id",
        key: "tipo_documento_id",
        render: (tipo_documento_id) => {
          const tipo = tiposDocumento.find(
            (tipo) => tipo.id === tipo_documento_id
          );
          return tipo ? tipo.descripcion : "Desconocido";
        },
        sorter: (a, b) => {
          const tipoA =
            tiposDocumento.find((tipo) => tipo.id === a.tipo_documento_id)
              ?.descripcion || "";
          const tipoB =
            tiposDocumento.find((tipo) => tipo.id === b.tipo_documento_id)
              ?.descripcion || "";
          return tipoA.localeCompare(tipoB);
        },
      },
      {
        title: "Número de Documento",
        dataIndex: "numero_documento",
        key: "numero_documento",
        sorter: (a, b) => a.numero_documento.localeCompare(b.numero_documento),
      },
      {
        title: "Ciudad, Departamento",
        dataIndex: "ciudad",
        key: "ciudad",
        render: (ciudad, record) => {
          const ciudadNombre = ciudad?.nombre || ciudad || "Sin Ciudad";
          const departamentoNombre =
            record.departamento?.nombre ||
            record.departamento ||
            "Sin Departamento";
          return `${ciudadNombre}, ${departamentoNombre}`;
        },
        sorter: (a, b) => {
          const ciudadA = `${a.ciudad?.nombre || a.ciudad || ""}, ${
            a.departamento?.nombre || a.departamento || ""
          }`;
          const ciudadB = `${b.ciudad?.nombre || b.ciudad || ""}, ${
            b.departamento?.nombre || b.departamento || ""
          }`;
          return ciudadA.localeCompare(ciudadB);
        },
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        render: (estado, driver) => {
          const { hasExpired, expiredDocs } = hasExpiredDocuments(driver);
          if (typeof estado === "string") {
            const estadoLower = estado.toLowerCase();
            let className = "status-tag-unknown";
            if (estadoLower === "activo") className = "status-tag-active";
            else if (estadoLower === "suspendido")
              className = "status-tag-suspended";
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={className}>
                  {estado.charAt(0).toUpperCase() + estado.slice(1)}
                </span>
                {hasExpired && (
                  <Tooltip title={`Documentos vencidos: ${expiredDocs.join(', ')}`}>
                    <WarningOutlined style={{ color: '#faad14', fontSize: 16 }} />
                  </Tooltip>
                )}
              </div>
            );
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="status-tag-unknown">Desconocido</span>
              {hasExpired && (
                <Tooltip title={`Documentos vencidos: ${expiredDocs.join(', ')}`}>
                  <WarningOutlined style={{ color: '#faad14', fontSize: 16 }} />
                </Tooltip>
              )}
            </div>
          );
        },
        sorter: (a, b) => a.estado.localeCompare(b.estado),
      },
      {
        title: "Vehículo Asignado",
        dataIndex: "vehiculo_asignado",
        key: "vehiculo_asignado",
        render: (vehiculo) => vehiculo
          ? `${vehiculo.codigo} - ${vehiculo.placa}`
          : <Tag color="default">Sin vehículo</Tag>,
        sorter: (a, b) =>
          (a.vehiculo_asignado?.placa || "").localeCompare(b.vehiculo_asignado?.placa || ""),
      },
      {
        title: "Acciones",
        key: "acciones",
        render: (text, driver) => {
          const items = [
            hasPermission("Transporte", "VISUALIZAR_T") && {
              key: "ver",
              icon: <EyeOutlined />,
              label: "Ver Conductor",
              onClick: () => handleViewClick(driver),
            },
            hasPermission("Transporte", "EDITAR_T") && {
              key: "editar",
              icon: <EditOutlined />,
              label: "Editar Conductor",
              onClick: () => handleEditClick(driver),
            },
            hasPermission("Transporte", "VISUALIZAR_T") && {
              key: "historial",
              icon: <HistoryOutlined />,
              label: "Historial",
              onClick: () => handleHistorialClick(driver),
            },
            hasPermission("Transporte", "VISUALIZAR_T") && {
              key: "historial-traslados",
              icon: <CarOutlined/>,
              label: "Historial de Traslados",
              onClick: () => {
                setSelectedConductorId(driver.id);
                setTransfersModalOpen(true);
              }
            },
            hasPermission("Transporte", "VISUALIZAR_T") && {
              key: "dispositivos",
              icon: <MobileOutlined />,
              label: "Dispositivos",
              onClick: () => {
                setSelectedConductorId(driver.id);
                setSelectedConductorNombre(driver.nombre);
                setDispositivosDialogOpen(true);
              }
            },
            hasPermission("Transporte", "EDITAR_T") && {
              key: "reiniciar-password",
              icon: <KeyOutlined />,
              label: "Reiniciar Contraseña",
              onClick: () => handleOpenResetPasswordDialog(driver),
            },
            hasPermission("Transporte", "EDITAR_T") && {
              key: "reiniciar-firma",
              icon: <HighlightOutlined />,
              label: "Reiniciar Firma",
              onClick: () => handleReiniciarFirma(driver),
            },
          ].filter(Boolean);

          return (
            <div className="driver-actions-dropdown">
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  icon={<EllipsisOutlined />}
                  type="text"
                  className="driver-actions-btn"
                />
              </Dropdown>
            </div>
          );
        },
        width: 100,
      },
    ],
    [
      tiposDocumento,
      handleEditClick,
      handleViewClick,
      handleHistorialClick,
      handleOpenResetPasswordDialog,
      handleReiniciarFirma,
      hasPermission,
      hasExpiredDocuments,
    ]
  );

  // Vista móvil: renderizar tarjeta de conductor
  const renderDriverCard = (driver) => {
    const items = [
      hasPermission("Transporte", "VISUALIZAR_T") && {
        key: "ver",
        icon: <EyeOutlined />,
        label: "Ver Conductor",
        onClick: () => handleViewClick(driver),
      },
      hasPermission("Transporte", "EDITAR_T") && {
        key: "editar",
        icon: <EditOutlined />,
        label: "Editar Conductor",
        onClick: () => handleEditClick(driver),
      },
      hasPermission("Transporte", "VISUALIZAR_T") && {
        key: "historial",
        icon: <HistoryOutlined />,
        label: "Historial",
        onClick: () => handleHistorialClick(driver),
      },
      hasPermission("Transporte", "VISUALIZAR_T") && {
        key: "dispositivos",
        icon: <MobileOutlined />,
        label: "Dispositivos",
        onClick: () => {
          setSelectedConductorId(driver.id);
          setSelectedConductorNombre(driver.nombre);
          setDispositivosDialogOpen(true);
        },
      },
      hasPermission("Transporte", "EDITAR_T") && {
        key: "reiniciar-password",
        icon: <KeyOutlined />,
        label: "Reiniciar Contraseña",
        onClick: () => handleOpenResetPasswordDialog(driver),
      },
      hasPermission("Transporte", "EDITAR_T") && {
        key: "reiniciar-firma",
        icon: <HighlightOutlined />,
        label: "Reiniciar Firma",
        onClick: () => handleReiniciarFirma(driver),
      },
    ].filter(Boolean);

    return (
      <Card key={driver.id} className="user-mobile-card">
        <div className="user-mobile-header">
          <div className="user-mobile-avatar">
            <Avatar size={48} className="user-mobile-avatar-placeholder">
              <UserOutlined />
            </Avatar>
          </div>
          <div className="user-mobile-info">
            <div className="user-mobile-name">{driver.nombre}</div>
            <div className="user-mobile-status" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {typeof driver.estado === "string" ? (
                <Tag
                  className={
                    driver.estado.toLowerCase() === "activo"
                      ? "status-tag-active"
                      : driver.estado.toLowerCase() === "suspendido"
                      ? "status-tag-suspended"
                      : "status-tag-unknown"
                  }
                >
                  {driver.estado.charAt(0).toUpperCase() +
                    driver.estado.slice(1)}
                </Tag>
              ) : (
                <Tag className="status-tag-unknown">Desconocido</Tag>
              )}
              {hasExpiredDocuments(driver).hasExpired && (
                <Tooltip title={`Docs vencidos: ${hasExpiredDocuments(driver).expiredDocs.join(', ')}`}>
                  <WarningOutlined style={{ color: '#faad14', fontSize: 14 }} />
                </Tooltip>
              )}
            </div>
          </div>
          <div className="user-mobile-actions">
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button
                icon={<EllipsisOutlined />}
                className="user-mobile-actions-btn"
                type="text"
              />
            </Dropdown>
          </div>
        </div>
        <div className="user-mobile-details">
          <div className="user-mobile-detail">
            <IdcardOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">
              {driver.numero_documento}
            </span>
          </div>
          <div className="user-mobile-detail">
            <TeamOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">
              {driver.tipo_documento_id
                ? tiposDocumento.find((t) => t.id === driver.tipo_documento_id)
                    ?.descripcion || "-"
                : "-"}
            </span>
          </div>
          <div className="user-mobile-detail">
            <EnvironmentOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">
              {driver.ciudad?.nombre || driver.ciudad || "Sin Ciudad"},{" "}
              {driver.departamento?.nombre ||
                driver.departamento ||
                "Sin Departamento"}
            </span>
          </div>
          <div className="user-mobile-dates">
            <div className="user-mobile-date">
              <CalendarOutlined className="user-mobile-date-icon" />
              <span className="user-mobile-date-text">
                Creado:{" "}
                {driver.fecha_creacion
                  ? new Date(driver.fecha_creacion).toLocaleDateString()
                  : "-"}
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  const isMobile = !screens.md;

  return (
    <div className="drivers-container">
      {/* Header Section */}
      <div className="drivers-header">
        <Typography.Title level={2} className="drivers-title">
          Gestión de Conductores
        </Typography.Title>
        <Typography.Text className="drivers-subtitle">
          Administra los conductores del sistema
        </Typography.Text>
        <div className="drivers-actions">
          <div className="drivers-search">
            <Search
              placeholder="Buscar conductor"
              enterButton={<SearchOutlined />}
              value={filter}
              onChange={handleSearchChange}
              style={{ width: "100%" }}
              allowClear
            />
          </div>
          <Select
            placeholder="Filtrar por Zona"
            allowClear
            style={{ width: 200 }}
            onChange={(value) => setSelectedZona(value)}
            loading={loadingZonas}
            value={selectedZona}
          >
            {zonas.map((zona) => (
              <Option key={zona.id} value={zona.nombre}>
                {zona.nombre}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Filtrar por Estado"
            allowClear
            style={{ width: 160 }}
            onChange={(value) => setSelectedEstado(value)}
            value={selectedEstado}
          >
            <Option value="Activo">
              <span className="status-tag-active">Activo</span>
            </Option>
            <Option value="Suspendido">
              <span className="status-tag-suspended">Suspendido</span>
            </Option>
            <Option value="Inactivo">
              <span className="status-tag-unknown">Inactivo</span>
            </Option>
          </Select>
          <Select
            placeholder="Filtrar por Vehículo"
            allowClear
            style={{ width: 170 }}
            onChange={(value) => setFilterConVehiculo(value)}
            value={filterConVehiculo}
          >
            <Option value="con">
              <Tag color="green">Con Vehículo</Tag>
            </Option>
            <Option value="sin">
              <Tag color="orange">Sin Vehículo</Tag>
            </Option>
          </Select>
          <Select
            placeholder="Conductor propio"
            allowClear
            style={{ width: 170 }}
            onChange={(value) => setFilterConductorPropio(value ?? null)}
            value={filterConductorPropio}
          >
            <Option value="si">
              <Tag color="green">Propio: Sí</Tag>
            </Option>
            <Option value="no">
              <Tag color="blue">Propio: No</Tag>
            </Option>
            <Option value="sin_definir">
              <Tag color="default">Sin definir</Tag>
            </Option>
          </Select>
          <Button
            type={filterDocVencidos ? "primary" : "default"}
            danger={filterDocVencidos}
            icon={<WarningOutlined />}
            onClick={() => setFilterDocVencidos(!filterDocVencidos)}
            style={{ marginRight: 8 }}
          >
            Docs. Vencidos
          </Button>
          {hasPermission("Transporte", "EXPORTAR_T") && (
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
              loading={exportLoading}
              style={{ marginRight: 8 }}
            >
              Exportar Excel
            </Button>
          )}
          {hasPermission("Transporte", "CREAR_T") && (
            <Button
              className="drivers-add-btn"
              icon={<PlusOutlined />}
              onClick={() => setAddDialogOpen(true)}
            >
              Agregar Conductor
            </Button>
          )}
        </div>
      </div>
      {/* Content Container */}
      <Spin
        spinning={listLoading}
        tip="Cargando conductores..."
        className="drivers-loading"
      >
        {isMobile ? (
          <div className="user-mobile-container">
            <div className="user-mobile-grid">
              {paginatedDrivers.map((driver) => renderDriverCard(driver))}
            </div>
            <div className="user-mobile-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={filteredDrivers.length}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={handleRowsPerPageChange}
                pageSizeOptions={["5", "10", "25"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total}`
                }
                size="small"
                simple
              />
            </div>
          </div>
        ) : (
          <div className="drivers-table-container">
            <Table
              className="drivers-table"
              columns={columns.map((col) => ({
                ...col,
                onHeaderCell: () => ({
                  style: { textAlign: "center" },
                }),
              }))}
              dataSource={paginatedDrivers}
              pagination={false}
              rowKey="id"
              bordered={false}
              scroll={{ y: 600 }}
            />
            <div className="drivers-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={filteredDrivers.length}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={handleRowsPerPageChange}
                pageSizeOptions={["5", "10", "25", "50", "100"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total} conductores`
                }
              />
            </div>
          </div>
        )}
      </Spin>
      {/* Formularios y Diálogos */}

      {/* Agregar Conductor - Faltaba protección */}
      {addDialogOpen && hasPermission("Transporte", "CREAR_T") && (
        <AddDriverForm
          open={addDialogOpen}
          handleClose={() => setAddDialogOpen(false)}
          fetchDrivers={fetchDrivers}
        />
      )}

      {/* Editar Conductor - Faltaba protección */}
      {editDialogOpen &&
        selectedDriver &&
        hasPermission("Transporte", "EDITAR_T") && (
          <AddDriverForm
            open={editDialogOpen}
            handleClose={() => setEditDialogOpen(false)}
            fetchDrivers={fetchDrivers}
            initialData={selectedDriver}
            isEdit={true}
          />
        )}

      {/* Ver Conductor - Faltaba protección */}
      {viewDialogOpen &&
        selectedDriver &&
        hasPermission("Transporte", "VISUALIZAR_T") && (
          <ViewDriverDialog
            driver={selectedDriver}
            open={viewDialogOpen}
            handleClose={() => setViewDialogOpen(false)}
          />
        )}

      {/* Diálogo del Historial del Conductor - Correcto */}
      {historialDialogOpen && hasPermission("Transporte", "VISUALIZAR_T") && (
        <HistorialConductorDialog
          open={historialDialogOpen}
          onClose={() => setHistorialDialogOpen(false)}
          conductorId={selectedConductorId}
        />
      )}

      {/* Diálogo del Historial de Traslados del conductor */}
      {transfersModalOpen && (
        <DriverTransfersModal
        conductorId={selectedConductorId}
        open={transfersModalOpen}
        onClose={() => setTransfersModalOpen(false)}
        />
      )}

      {/* Diálogo de Dispositivos del conductor */}
      {dispositivosDialogOpen && (
        <HistorialDispositivosDialog
          open={dispositivosDialogOpen}
          onClose={() => setDispositivosDialogOpen(false)}
          conductorId={selectedConductorId}
          conductorNombre={selectedConductorNombre}
        />
      )}

      {/* Confirmación de Reinicio de Contraseña */}
      {resetPasswordDialogOpen && hasPermission("Transporte", "EDITAR_T") && (
        <Modal
          title="Reiniciar Contraseña"
          open={resetPasswordDialogOpen}
          onOk={confirmResetPassword}
          onCancel={cancelResetPassword}
          okText="Reiniciar"
          cancelText="Cancelar"
          okButtonProps={{ loading: resetPasswordLoading }}
          cancelButtonProps={{ disabled: resetPasswordLoading }}
        >
          <Text>
            ¿Estás seguro de que deseas reiniciar la contraseña del conductor{" "}
            <strong>{driverToResetPassword?.nombre || ""}</strong>?
          </Text>
          <br /><br />
          <Text type="secondary">
            La nueva contraseña será: <strong>mogotax123</strong>
          </Text>
          <br />
          <Text type="secondary">
            El conductor deberá cambiarla en su próximo inicio de sesión.
          </Text>
        </Modal>
      )}
    </div>
  );
};

export default Drivers;
